/**
 * Stream B — B46: First Client During Signup
 *
 * After agency signup, immediately guide to activate first client.
 * Redirects to activation wizard, then to command center.
 * Real apiFetch. No mocks. CSS variables only.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import { apiFetch } from "@/lib/api-client";
import { Zap, Users, ArrowRight, CheckCircle2 } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useTranslation } from "@/i18n";
import { MSG } from "@ease/shared/i18n/message-keys";

interface AgencyState {
  clientCount: number;
  isNewAgency: boolean;
}

export default function FirstClientPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<AgencyState | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchState = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: AgencyState }>("/agency/state");
      setState(res.data);
      // If already has clients, redirect to dashboard
      if (res.data.clientCount > 0) {
        navigate("/agency", { replace: true });
      }
    } catch {
      setState({ clientCount: 0, isNewAgency: true });
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchState(); }, [fetchState]);

  if (isLoading) {
    return (
      <div className="max-w-xl mx-auto py-16 space-y-4">
        <Skeleton className="mx-auto h-8 w-64" />
        <SkeletonCard className="h-48" />
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto py-16 text-center space-y-8">
      <div>
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[var(--color-primary)]/10">
          <Users className="h-8 w-8 text-[var(--color-primary)]" />
        </div>
        <h1 className="text-3xl font-bold text-[var(--color-foreground)]">{t(MSG.agency.firstClient.title)}</h1>
        <p className="mt-2 text-[var(--color-muted-foreground)]">
          {t(MSG.agency.firstClient.description)}
        </p>
      </div>

      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6 text-left space-y-4">
        <h2 className="text-lg font-semibold text-[var(--color-foreground)]">{t(MSG.agency.firstClient.nextTitle)}</h2>
        {[
          { icon: Zap, text: t(MSG.agency.firstClient.steps.businessInfo) },
          { icon: CheckCircle2, text: t(MSG.agency.firstClient.steps.preconfigure) },
          { icon: Users, text: t(MSG.agency.firstClient.steps.dashboard) },
        ].map(({ icon: Icon, text }, i) => (
          <div key={i} className="stagger-item flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--color-primary)]/10 shrink-0">
              <Icon className="h-4 w-4 text-[var(--color-primary)]" />
            </div>
            <p className="text-sm text-[var(--color-foreground)]">{text}</p>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => navigate("/agency/activate")}
        className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-6 py-3 text-sm font-medium text-white hover:opacity-90"
      >
        {t(MSG.agency.firstClient.cta)}
        <ArrowRight className="h-4 w-4" />
      </button>

      <button
        type="button"
        onClick={() => navigate("/agency")}
        className="block mx-auto text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
      >
        {t(MSG.agency.firstClient.skip)}
      </button>
    </div>
  );
}
